/**
 * MasterLibraryBrowseModal — Clinic-side "+ Add from Library" flow.
 *
 * Opened from ProductCataloguePage's "+ Add from Library" button.
 * Lets the clinic search the curated library, filter by form/tier/brand,
 * multi-select rows (including a "select all filtered" quick-action that
 * spans pagination), and adopt them into ha_products.
 *
 * 2026-02-19 · Added Bulk Adopt Quotas:
 *   - `Select all N filtered` chip in the footer that fetches every row
 *     matching the current filter (up to 500) and ticks them in one shot.
 *   - Confirmation dialog when the selection > BULK_CAP so a receptionist
 *     can't accidentally slurp the entire 1,158-row library.
 *   - Batched adopt loop with a live `X of N added…` progress bar so a
 *     409-duplicate or network blip doesn't kill the entire onboarding.
 *
 * Strict scope: adopt only writes to ha_products (SKU shell); no stock,
 * no serial, no accessory rows are created here.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { X, Search, Check, Loader2, Library, PackagePlus, ListChecks, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FORM_OPTIONS = [
  'BTE', 'RIC', 'Slim-RIC', 'CIC', 'IIC', 'ITC', 'ITE',
  'Instant Fit CIC', 'Instant Fit ITE', 'CROS', 'Accessory', 'Consumable',
];
const TIER_OPTIONS = ['Ultra', 'Premium', 'Advanced', 'Standard', 'Essential', 'Basic', 'Economy'];
const BULK_CAP = 25;                     // soft-cap for one adopt click
const SELECT_ALL_HARD_CAP = 500;         // absolute ceiling per select-all fetch

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;


export default function MasterLibraryBrowseModal({ onClose, onAdopted }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [distinctBrands, setDistinctBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [brand, setBrand] = useState('');
  const [form, setForm] = useState('');
  const [tier, setTier] = useState('');
  // selected: Map<master_id, {brand,model}>  — keeps brand/model even for
  // rows that live on other pagination pages so batched adopt can render
  // proper labels in the feedback stream.
  const [selected, setSelected] = useState(() => new Map());
  const [adopting, setAdopting] = useState(false);
  const [progress, setProgress] = useState(null);   // {done, total}
  const [confirmBulk, setConfirmBulk] = useState(null);   // pending count awaiting confirmation
  const [feedback, setFeedback] = useState([]);   // [{brand, model, status, message}]
  const [skip, setSkip] = useState(0);
  const [limit] = useState(50);
  const [selectingAll, setSelectingAll] = useState(false);

  const filtersActive = !!(search || brand || form || tier);

  const _filterParams = () => {
    const p = {};
    if (search) p.search = search;
    if (brand)  p.brand = brand;
    if (form)   p.form = form;
    if (tier)   p.tier = tier;
    return p;
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/master-products`, {
        params: { limit, skip, ..._filterParams() },
      });
      setRows(r.data?.rows || []);
      setTotal(r.data?.total || 0);
      setDistinctBrands(r.data?.distinct_brands || []);
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [search, brand, form, tier, skip]);

  const toggle = (row) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(row.master_id)) next.delete(row.master_id);
      else next.set(row.master_id, { brand: row.brand, model: row.model });
      return next;
    });
  };

  const selectAllFiltered = async () => {
    setSelectingAll(true);
    try {
      const r = await axios.get(`${API}/master-products`, {
        params: { limit: SELECT_ALL_HARD_CAP, skip: 0, ..._filterParams() },
      });
      const fetched = r.data?.rows || [];
      setSelected((prev) => {
        const next = new Map(prev);
        for (const row of fetched) {
          next.set(row.master_id, { brand: row.brand, model: row.model });
        }
        return next;
      });
    } catch {
      /* ignore */
    } finally {
      setSelectingAll(false);
    }
  };

  const clearSelection = () => setSelected(new Map());

  const performAdopt = async () => {
    const entries = Array.from(selected.entries());
    setAdopting(true);
    setFeedback([]);
    setProgress({ done: 0, total: entries.length });
    const results = [];
    for (let i = 0; i < entries.length; i++) {
      const [id, meta] = entries[i];
      try {
        const r = await axios.post(`${API}/master-products/${id}/adopt`);
        results.push({
          brand: meta.brand, model: meta.model,
          status: r.data?.status || 'adopted',
          message: r.data?.message || 'Added',
        });
      } catch (e) {
        results.push({
          brand: meta.brand, model: meta.model,
          status: 'error',
          message: e?.response?.data?.detail || 'Failed',
        });
      }
      setProgress({ done: i + 1, total: entries.length });
      // Live-append the newest result so the sidebar isn't blank during a long batch.
      setFeedback([...results]);
    }
    setSelected(new Map());
    setAdopting(false);
    setProgress(null);
    if (onAdopted) onAdopted(results);
  };

  const adoptAll = async () => {
    if (selected.size === 0) return;
    if (selected.size > BULK_CAP) {
      setConfirmBulk(selected.size);
      return;
    }
    await performAdopt();
  };

  const confirmAndAdopt = async () => {
    setConfirmBulk(null);
    await performAdopt();
  };

  const closeAndReload = () => {
    if (feedback.some((r) => r.status === 'adopted') && onAdopted) onAdopted(feedback);
    onClose();
  };

  const summary = useMemo(() => {
    if (!feedback.length) return null;
    let added = 0, dup = 0, err = 0;
    for (const r of feedback) {
      if (r.status === 'adopted') added++;
      else if (r.status === 'duplicate') dup++;
      else err++;
    }
    return { added, dup, err };
  }, [feedback]);

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-start justify-center p-3 pt-10" data-testid="master-library-browse">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl flex flex-col max-h-[88vh]">
        <header className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-700">
              <Library size={16} strokeWidth={2.2} />
            </div>
            <div>
              <div className="text-sm font-bold text-slate-800">Master Product Library</div>
              <div className="text-[11px] text-slate-500">Pick any brand / model and add to your catalogue. No stock is created — just the SKU shell.</div>
            </div>
          </div>
          <button onClick={closeAndReload} data-testid="mlb-close" className="w-7 h-7 rounded hover:bg-slate-100 flex items-center justify-center text-slate-500"><X size={14} /></button>
        </header>

        <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="mlb-search"
              placeholder="Search brand / model…"
              value={search}
              onChange={(e) => { setSkip(0); setSearch(e.target.value); }}
              className="pl-7 pr-2 py-1.5 text-xs border border-slate-300 rounded w-full"
            />
          </div>
          <select data-testid="mlb-brand" value={brand} onChange={(e) => { setSkip(0); setBrand(e.target.value); }}
            className="px-2 py-1.5 text-xs border border-slate-300 rounded bg-white">
            <option value="">All brands</option>
            {distinctBrands.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
          <select data-testid="mlb-form" value={form} onChange={(e) => { setSkip(0); setForm(e.target.value); }}
            className="px-2 py-1.5 text-xs border border-slate-300 rounded bg-white">
            <option value="">All forms</option>
            {FORM_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
          <select data-testid="mlb-tier" value={tier} onChange={(e) => { setSkip(0); setTier(e.target.value); }}
            className="px-2 py-1.5 text-xs border border-slate-300 rounded bg-white">
            <option value="">All tiers</option>
            {TIER_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        <div className="flex-1 overflow-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 sticky top-0 text-[10px] uppercase tracking-wider text-slate-600 border-b border-slate-200">
              <tr>
                <th className="px-3 py-2 w-8"></th>
                <th className="px-3 py-2 text-left">Brand</th>
                <th className="px-3 py-2 text-left">Model</th>
                <th className="px-3 py-2 text-left">Form</th>
                <th className="px-3 py-2 text-left">Tier</th>
                <th className="px-3 py-2 text-right">MRP</th>
                <th className="px-3 py-2 text-center">Sale Unit</th>
                <th className="px-3 py-2 text-right">Wty (m)</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="py-10 text-center text-slate-400"><Loader2 size={16} className="inline animate-spin mr-1" /> Loading library…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={8} className="py-10 text-center text-slate-400 italic">No products match these filters.</td></tr>
              )}
              {!loading && rows.map(p => (
                <tr key={p.master_id} data-testid={`mlb-row-${p.master_id}`}
                  className={`border-t border-slate-100 cursor-pointer hover:bg-indigo-50/50 ${selected.has(p.master_id) ? 'bg-indigo-100/70' : ''}`}
                  onClick={() => toggle(p)}>
                  <td className="px-3 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={selected.has(p.master_id)}
                      readOnly
                      data-testid={`mlb-check-${p.master_id}`}
                    />
                  </td>
                  <td className="px-3 py-2 font-semibold">{p.brand}</td>
                  <td className="px-3 py-2">{p.model}</td>
                  <td className="px-3 py-2"><span className="bg-slate-100 rounded px-1.5 py-0.5">{p.form}</span></td>
                  <td className="px-3 py-2 capitalize">{p.tier || '—'}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtINR(p.mrp)}</td>
                  <td className="px-3 py-2 text-center"><span className="bg-indigo-50 text-indigo-700 rounded px-1.5 py-0.5 text-[10px]">{p.sale_unit}</span></td>
                  <td className="px-3 py-2 text-right">{p.warranty_months ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {progress && (
          <div className="px-5 py-2 border-t border-slate-100 bg-indigo-50" data-testid="mlb-progress">
            <div className="flex items-center justify-between text-[11px] font-semibold text-indigo-800 mb-1">
              <span>Adding to your catalogue…</span>
              <span className="tabular-nums">{progress.done} / {progress.total}</span>
            </div>
            <div className="h-1.5 bg-indigo-100 rounded overflow-hidden">
              <div className="h-full bg-indigo-600 transition-all"
                style={{ width: `${progress.total ? Math.round((progress.done / progress.total) * 100) : 0}%` }} />
            </div>
          </div>
        )}

        {feedback.length > 0 && !progress && (
          <div className="px-5 py-2 border-t border-slate-100 text-[11px] bg-slate-50" data-testid="mlb-feedback">
            {summary && (
              <div className="mb-1 font-semibold text-slate-700">
                <span className="text-emerald-700">{summary.added} added</span>
                <span className="mx-1 text-slate-400">·</span>
                <span className="text-amber-700">{summary.dup} already in catalogue</span>
                {summary.err > 0 && <>
                  <span className="mx-1 text-slate-400">·</span>
                  <span className="text-rose-700">{summary.err} failed</span>
                </>}
              </div>
            )}
            <div className="max-h-[110px] overflow-auto">
              {feedback.map((r, i) => (
                <div key={i} className={`py-0.5 ${r.status === 'adopted' ? 'text-emerald-700' : r.status === 'duplicate' ? 'text-amber-700' : 'text-rose-700'}`}>
                  <span className="font-semibold">{r.brand} · {r.model}</span> — {r.message}
                </div>
              ))}
            </div>
          </div>
        )}

        <footer className="px-5 py-3 border-t border-slate-200 flex flex-wrap items-center gap-2 justify-between">
          <div className="flex items-center gap-2 text-[11px] text-slate-500">
            <span><span className="font-semibold text-slate-700">{selected.size}</span> selected · Total {total}</span>
            {total > rows.length && filtersActive && selected.size < total && (
              <button
                data-testid="mlb-select-all-filtered"
                onClick={selectAllFiltered}
                disabled={selectingAll || adopting}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-indigo-300 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-semibold text-[10px] disabled:opacity-50">
                {selectingAll ? <><Loader2 size={10} className="animate-spin" /> Selecting…</> : <><ListChecks size={10} /> Select all {Math.min(total, SELECT_ALL_HARD_CAP)} filtered</>}
              </button>
            )}
            {selected.size > 0 && (
              <button
                data-testid="mlb-clear-selection"
                onClick={clearSelection}
                disabled={adopting}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-slate-300 bg-white hover:bg-slate-50 text-slate-600 font-semibold text-[10px] disabled:opacity-50">
                <XCircle size={10} /> Clear
              </button>
            )}
            {selected.size > BULK_CAP && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-800 font-semibold text-[10px]">
                Above the {BULK_CAP}-per-click safety cap — you'll be asked to confirm
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setSkip(Math.max(0, skip - limit))} disabled={skip <= 0}
              className="px-2 py-1 text-[11px] border border-slate-300 rounded bg-white disabled:opacity-40">Prev</button>
            <span className="text-[11px] text-slate-500">{Math.min(skip + 1, Math.max(total, 1))}–{Math.min(skip + limit, total)}</span>
            <button onClick={() => setSkip(skip + limit)} disabled={skip + limit >= total}
              className="px-2 py-1 text-[11px] border border-slate-300 rounded bg-white disabled:opacity-40">Next</button>
            <button
              onClick={adoptAll}
              disabled={selected.size === 0 || adopting}
              data-testid="mlb-adopt"
              className="ml-2 inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white rounded shadow-sm disabled:opacity-50">
              {adopting ? <><Loader2 size={12} className="animate-spin" /> Adding…</> : <><PackagePlus size={12} /> Add {selected.size > 0 ? selected.size : ''} to My Catalogue</>}
            </button>
          </div>
        </footer>

        {confirmBulk !== null && (
          <div className="absolute inset-0 z-[90] bg-black/40 flex items-center justify-center p-6" data-testid="mlb-bulk-confirm">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-sm p-5">
              <div className="text-sm font-bold text-slate-800 mb-1">Add {confirmBulk} SKUs at once?</div>
              <div className="text-[11px] text-slate-600 mb-4">
                That's above the <b>{BULK_CAP}-per-click</b> safety cap. Bulk imports are best for
                clinic onboarding — accidental full-library adds are much harder to undo.
                We'll add them in batches with a live progress bar.
              </div>
              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={() => setConfirmBulk(null)}
                  className="px-3 py-1.5 text-xs border border-slate-300 rounded bg-white hover:bg-slate-50">
                  Cancel
                </button>
                <button
                  onClick={confirmAndAdopt}
                  data-testid="mlb-bulk-confirm-go"
                  className="px-3 py-1.5 text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white rounded">
                  Yes, add {confirmBulk} SKUs
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
